import type { MailLog, MailLogsParams, MailLogsResponse } from '../types'

/** Parse `YYYY-MM-DD` as local midnight (avoids UTC shift in `new Date(iso)`). */
export function parseIsoDateLocal(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null
  return new Date(y, mo - 1, d)
}

/**
 * Backend payloads vary; normalize so the list always gets `records` + `total`.
 * Angular expects `{ records, total }`.
 */
export function normalizeMailLogsResponse(payload: unknown): MailLogsResponse {
  if (payload == null || typeof payload !== 'object') {
    return { records: [], total: 0 }
  }
  const p = payload as Record<string, unknown>
  let records: MailLog[] = []
  if (Array.isArray(p.records)) {
    records = p.records as MailLog[]
  } else if (Array.isArray(p.data)) {
    records = p.data as MailLog[]
  }
  const totalRaw = p.total
  let total = 0
  if (typeof totalRaw === 'number' && Number.isFinite(totalRaw)) {
    total = totalRaw
  } else if (typeof totalRaw === 'string') {
    const n = Number.parseInt(totalRaw, 10)
    total = Number.isFinite(n) ? n : records.length
  } else {
    total = records.length
  }
  return { records, total }
}

/** Omit undefined/null so axios does not send `"undefined"` query values. */
/** Avoid redundant `GET /api/mail_logs` when debounced inputs match applied params. */
export function mailtrapParamsEqual(a: MailLogsParams, b: MailLogsParams): boolean {
  return (
    (a.page ?? 1) === (b.page ?? 1) &&
    (a.perPage ?? 15) === (b.perPage ?? 15) &&
    (a.to ?? '') === (b.to ?? '') &&
    (a.lr_form ?? '') === (b.lr_form ?? '') &&
    (a.fromDt ?? '') === (b.fromDt ?? '') &&
    (a.toDt ?? '') === (b.toDt ?? '') &&
    (a.type ?? '') === (b.type ?? '')
  )
}

export function compactMailLogsParams(params: MailLogsParams): MailLogsParams {
  const next: MailLogsParams = {
    page: params.page ?? 1,
    perPage: params.perPage ?? 15,
  }
  if (params.to != null && String(params.to).trim() !== '') {
    next.to = String(params.to).trim()
  }
  if (params.lr_form != null && String(params.lr_form).trim() !== '') {
    next.lr_form = String(params.lr_form).trim()
  }
  if (params.fromDt) next.fromDt = params.fromDt
  if (params.toDt) next.toDt = params.toDt
  if (params.type) next.type = params.type
  return next
}
